// janus-engine/inventory/mesh-scale.ts — which placed inventory rows render at their `inventory_size`.
//
// PURE. The rule the FT3D scene applies (owner 2026-09-10: "use common sense"), from the family audit of every
// placed row carrying an override:
//   ratio = base_size(instance size) / base_size(explicit authoring basis, otherwise TEMPLATE catalog size)
// applied ONLY when the row draws its OWN native mesh — most meshes use the template's default size, so
// the ratio is exactly once. Everything else keeps 1:
//   - an absolute transform (its scale already IS the drawn size),
//   - a standee (its width already comes from the instance size — the ratio would double it),
//   - a review/prop override mesh, or the meshless rubble fallback,
//   - wall runs and roofs (drawn by the room), doors / windows / curtains (the OPENING is the size authority; the
//     ratio would only thicken a fitted door's leaf and grow its footprint).
// ⛔ The denominator is never the row's own `size` layer: build_inventory_arr() materializes the override INTO it,
//    so on a server-compiled row the two are equal (the reason the ratio was silently 1 for every row until today).

import { encounterField } from '../../shared/array-types.js';

export interface SizeRatioEligibility {
  hasNativeMesh: boolean;
  isOverrideMesh: boolean;
  isRubbleFallback: boolean;
  absoluteTransform: boolean;
  itemType: string;
}

const OPENING_OR_STRUCTURE = new Set(['wall', 'roof', 'door', 'window', 'curtain']);

export function inventorySizeRatioEligible(input: SizeRatioEligibility): boolean {
  return input.hasNativeMesh
    && !input.isOverrideMesh
    && !input.isRubbleFallback
    && !input.absoluteTransform
    && !OPENING_OR_STRUCTURE.has(String(input.itemType || '').toLowerCase());
}

/**
 * Owner 2026-09-11 ("cap at the ceiling"): a resized object never pokes through the ceiling of the room it stands in.
 * The whole scale shrinks just enough to fit the clear height — proportions kept — and never below the object's
 * unresized scale (`baseScale`), so only the size ratio is ever given back. Open sky / unknown height: unchanged.
 */
export function ceilingCappedScale(scale: number, baseScale: number, meshHeightM: number | null, clearanceFt: number | null): number {
  if (!(scale > baseScale) || meshHeightM === null || !(meshHeightM > 0) || clearanceFt === null || !(clearanceFt > 0)) return scale;
  return Math.max(baseScale, Math.min(scale, clearanceFt * 0.3048 / meshHeightM));
}

/** base_size(instance) / base_size(template default), clamped to the scene's 1/16..16 band; 1 when either is unknown. */
export function inventorySizeRatio(
  instanceSizeId: string | null | undefined,
  templateSizeId: string | null | undefined,
  baseSizeFt: (sizeId: string) => number | null,
): number {
  const inst = String(instanceSizeId ?? '').trim().toUpperCase();
  const tpl = String(templateSizeId ?? '').trim().toUpperCase();
  if (inst === '' || tpl === '' || inst === tpl) return 1;
  const a = baseSizeFt(inst);
  const b = baseSizeFt(tpl);
  if (!(a !== null && a > 0) || !(b !== null && b > 0)) return 1;
  return Math.min(16, Math.max(0.0625, a / b));
}

/** Consolidated looks retain their own authoring basis; NULL instances inherit the canonical default. */
export function inventoryMeshSizeRatio(
  instanceSizeId: string | null | undefined,
  templateSizeId: string | null | undefined,
  authoredSizeTypeId: string | null | undefined,
  baseSizeFt: (sizeId: string) => number | null,
): number {
  const authored = String(authoredSizeTypeId ?? '').trim();
  return authored
    ? inventorySizeRatio(instanceSizeId || templateSizeId, authored, baseSizeFt)
    : inventorySizeRatio(instanceSizeId, templateSizeId, baseSizeFt);
}
/** One effective uniform scale for the renderer and every prop-space rule reader. */
export function effectivePropScale(baseScale: number, sizeRatio: number, meshHeightM: number | null, clearanceFt: number | null): number {
  return ceilingCappedScale(baseScale * sizeRatio, baseScale, meshHeightM, clearanceFt);
}

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Read the page's authored shape through its canonical sparse/compatibility reader. */
export function encounterShapeValue(encounter: Row): unknown {
  return encounterField<unknown>(encounter, 'encounter_shape');
}

/** Physical ceiling of the highest room at/below the prop's persisted contact plane; open sky stays uncapped. */
export function clearanceAboveFt(rawShape: unknown, x: number, y: number, z: number): number | null {
  let shape = rawShape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape); } catch { return null; } }
  const boxes = record(shape)?.boxes;
  if (!Array.isArray(boxes)) return null;
  let best: { floor: number; ceiling: number } | null = null;
  for (const candidate of boxes) {
    const box = record(candidate);
    const bx = record(box?.x), by = record(box?.y);
    const floor = Number(box?.floor);
    const ceiling = record(box?.creation)?.physicalCeilingFt;
    if (!bx || !by || !Number.isFinite(floor) || typeof ceiling !== 'number' || !Number.isFinite(ceiling)) continue;
    if (x < Number(bx.min) || x > Number(bx.max) || y < Number(by.min) || y > Number(by.max)) continue;
    if (floor > z + 0.5 || ceiling <= z) continue;
    if (!best || floor > best.floor) best = { floor, ceiling };
  }
  return best ? best.ceiling - z : null;
}
